/** Fallback values matching api-server platform_constants seed (offline / loading). */
export const DEFAULT_PLATFORM_CONSTANTS = {
  "design_request.max_items": 6,
  "design_request.max_locations_per_item": 4,
  "design_request.max_decorating_entries_per_item": 4,
  "design_request.review_link_ttl_days": 90,
  "design_request.estimate_quantity": { min: 0, max: 99999 },
  "quantity.default": { min: 1, max: 999 },
  "pagination.default_page_size": 25,
  "pagination.max_page_size": 100,
  "pagination.options_default_page_size": 25,
  "pagination.options_max_page_size": 50,
  "order.apparel_sizes": [
    "2XS",
    "XS",
    "S",
    "M",
    "L",
    "XL",
    "2XL",
    "3XL",
    "4XL",
    "5XL",
  ],
  "order.products_per_page": 5,
  "feature.admin_dashboard_stats": false,
  /** INVENTORY_ENABLED switch (schema doc 9.11). Off until the server says true. */
  "feature.inventory_enabled": false,
  "upload.design-request-inspiration": {
    key: "design-request-inspiration",
    folder: "design-requests/inspiration/",
    label: "Design request inspiration",
    maxFiles: 10,
    maxBytesPerFile: 50 * 1024 * 1024,
    allowedContentTypes: [
      "image/jpeg",
      "image/png",
      "image/svg+xml",
      "image/webp",
      "image/gif",
      "application/pdf",
    ],
  },
  "upload.design-request-proof": {
    key: "design-request-proof",
    folder: "design-requests/proofs/",
    label: "Design request proof",
    maxFiles: 5,
    maxBytesPerFile: 50 * 1024 * 1024,
    allowedContentTypes: [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
      "application/pdf",
    ],
  },
  "upload.order-design": {
    key: "order-design",
    folder: "orders/",
    label: "Order design file",
    maxFiles: 10,
    maxBytesPerFile: 25 * 1024 * 1024,
    allowedContentTypes: [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
      "application/pdf",
    ],
  },
  "upload.order-item-dst": {
    key: "order-item-dst",
    folder: "dst/",
    label: "Order item DST file",
    maxFiles: 20,
    maxBytesPerFile: 50 * 1024 * 1024,
    allowedContentTypes: [
      "application/octet-stream",
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
      "application/pdf",
    ],
  },
  "upload.avatar": {
    key: "avatar",
    folder: "avatars/",
    label: "User avatar",
    maxFiles: 1,
    maxBytesPerFile: 5 * 1024 * 1024,
    allowedContentTypes: ["image/jpeg", "image/png", "image/webp"],
  },
  "upload.note-attachment": {
    key: "note-attachment",
    folder: "notes/attachments/",
    label: "Note attachment",
    maxFiles: 10,
    maxBytesPerFile: 50 * 1024 * 1024,
    allowedContentTypes: [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
      "application/pdf",
    ],
  },
  "upload.note-reply-attachment": {
    key: "note-reply-attachment",
    folder: "notes/reply-attachments/",
    label: "Note reply attachment",
    maxFiles: 10,
    maxBytesPerFile: 50 * 1024 * 1024,
    allowedContentTypes: [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
      "application/pdf",
    ],
  },
  "upload.participants-csv": {
    key: "participants-csv",
    folder: "participants_csv/",
    label: "Order participant roster CSV",
    maxFiles: 1,
    maxBytesPerFile: 2 * 1024 * 1024,
    allowedContentTypes: ["text/csv", "application/vnd.ms-excel", "text/plain"],
  },
  "upload.custom-names-csv": {
    key: "custom-names-csv",
    folder: "custom_names_csv/",
    label: "Order custom names and numbers CSV",
    maxFiles: 1,
    maxBytesPerFile: 2 * 1024 * 1024,
    allowedContentTypes: ["text/csv", "application/vnd.ms-excel", "text/plain"],
  },
} as const;

export const DEFAULT_DESIGN_REQUEST_LIMITS = designRequestLimitsFromConstants(
  mergePlatformConstants(null),
);

export type PlatformConstantKey = keyof typeof DEFAULT_PLATFORM_CONSTANTS;

export type PlatformConstantsMap = Record<string, unknown>;

export type PlatformConstantsResponse = {
  constants: PlatformConstantsMap;
  byScope: Record<string, PlatformConstantsMap>;
  options?: import("@/constants").DomainOptionsDto;
};

export type UploadCatalogConfig = {
  key: string;
  folder: string;
  label: string;
  maxFiles: number;
  maxBytesPerFile: number;
  allowedContentTypes: string[];
};

export type DesignRequestLimits = {
  maxItems: number;
  maxLocationsPerItem: number;
  maxDecoratingEntriesPerItem: number;
  estimateQuantityMin: number;
  estimateQuantityMax: number;
  defaultQuantityMin: number;
  defaultQuantityMax: number;
};

export function designRequestLimitsFromConstants(
  constants: PlatformConstantsMap,
): DesignRequestLimits {
  const estimate = constants["design_request.estimate_quantity"] as
    | { min?: number; max?: number }
    | undefined;
  const qty = constants["quantity.default"] as
    | { min?: number; max?: number }
    | undefined;
  const defaults = DEFAULT_PLATFORM_CONSTANTS;

  return {
    maxItems: Number(
      constants["design_request.max_items"] ?? defaults["design_request.max_items"],
    ),
    maxLocationsPerItem: Number(
      constants["design_request.max_locations_per_item"] ??
        defaults["design_request.max_locations_per_item"],
    ),
    maxDecoratingEntriesPerItem: Number(
      constants["design_request.max_decorating_entries_per_item"] ??
        defaults["design_request.max_decorating_entries_per_item"],
    ),
    estimateQuantityMin: Number(
      estimate?.min ?? defaults["design_request.estimate_quantity"].min,
    ),
    estimateQuantityMax: Number(
      estimate?.max ?? defaults["design_request.estimate_quantity"].max,
    ),
    defaultQuantityMin: Number(qty?.min ?? defaults["quantity.default"].min),
    defaultQuantityMax: Number(qty?.max ?? defaults["quantity.default"].max),
  };
}

export function getUploadConfig(
  constants: PlatformConstantsMap,
  uploadKey: string,
): UploadCatalogConfig {
  const value =
    constants[`upload.${uploadKey}`] ??
    DEFAULT_PLATFORM_CONSTANTS[
      `upload.${uploadKey}` as keyof typeof DEFAULT_PLATFORM_CONSTANTS
    ];
  return value as UploadCatalogConfig;
}

export function mergePlatformConstants(
  api?: PlatformConstantsResponse | null,
): PlatformConstantsMap {
  return {
    ...DEFAULT_PLATFORM_CONSTANTS,
    ...(api?.constants ?? {}),
  };
}
